import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { formatHtmlFragment } from '@utils/formatCode';

/**
 * A readable code sample (REV-544).
 *
 * Captured `element_html` is usually one long minified line. The old blocks
 * rendered it raw inside a `<pre>` with `overflow-x: auto`, which produced two
 * problems: it read as an unbroken wall of markup, and the scroll container was
 * not focusable, so a keyboard user could not reach the off-screen part at all
 * (WCAG 2.1.1 — the same defect REV-507 fixed for tables).
 *
 * The fix for the second is NOT the REV-507 pattern. Measured: `pre-wrap` plus
 * `break-word` takes scrollWidth from 955px to exactly clientWidth even with a
 * 100-character unbreakable attribute — the block never scrolls horizontally at
 * all. There is therefore no scroll container to make focusable, and adding
 * `tabIndex` + `role="region"` anyway would only contribute dead tab stops and
 * landmarks for content that cannot scroll. Wrapping removes the failure rather
 * than mitigating it.
 *
 * Formatting sits on top as a progressive enhancement: the raw capture renders
 * immediately and the pretty-printed version swaps in when Prettier's ~260KB
 * dynamic import resolves.
 */
export interface CodeBlockProps {
  /** Raw captured or authored markup. */
  code: string;
  /** Skip formatting for content that is not HTML. */
  format?: boolean;
  /** Border colour, so failing/passing examples keep their visual distinction. */
  borderColor?: string;
}

export default function CodeBlock({
  code,
  format = true,
  borderColor = 'divider',
}: CodeBlockProps) {
  const [text, setText] = useState(code);

  useEffect(() => {
    setText(code);
    if (!format) return undefined;

    let cancelled = false;
    // No .catch: formatHtmlFragment cannot reject — it try/catches internally
    // and returns the raw source when Prettier fails to load or parse. `void`
    // records that the result is intentionally not awaited here.
    void formatHtmlFragment(code).then((formatted) => {
      if (!cancelled) setText(formatted);
    });
    return () => {
      cancelled = true;
    };
  }, [code, format]);

  return (
    <Box
      component="pre"
      sx={{
        mt: 2,
        p: 2,
        bgcolor: 'background.default',
        border: 1,
        borderColor,
        borderRadius: 1,
        fontSize: '0.85rem',
        // Wrap rather than scroll — see the note above. `pre-wrap` keeps the
        // formatter's indentation; `break-word` handles a single long token
        // (a data URI, a huge class list) that would otherwise still overflow.
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      <code>{text}</code>
    </Box>
  );
}
